import { useMemo } from 'react';

import { useCFPValues } from '@bcp-channels-sharedcomponents-reactjs/cf-proxy';
import { Alert, Box } from '@bcp-nextgen-dx-component-factory/design-system';
import { AppSegmentedInput, Captcha, CollapsibleBlock, CollapsibleBlockLink, InvalidDataAlert } from '@components';
import { AppHelper, UIHelper, UrlHelper } from '@helpers';
import { useLoginForm, useTranslation } from '@hooks';
import { useBlockStepManager } from '@modules';
import { LoginBlock, useSearchParamsStore, useServicesStore } from '@store';
import { SafeCredentialTypeEnum } from '@types';

const resources = {
	title: 'screens.login.form.multichannel.title',
	tooltip: {
		title: 'screens.login.form.multichannel.tooltip.title',
		description: 'screens.login.form.multichannel.tooltip.description',
	},
	links: {
		corporate: {
			text: 'screens.login.form.multichannel.links.corporate.text',
			url: 'screens.login.form.multichannel.links.corporate.url',
		},
		retail: 'screens.login.form.multichannel.links.retail',
	},
	info: {
		text: 'screens.login.form.multichannel.info.text',
	},
	closed: {
		title: 'screens.login.form.multichannel.closed.title',
		link: {
			text: 'screens.login.form.multichannel.closed.link.text',
		},
	},
	button: {
		continue: 'screens.login.button.continue',
	},
};

export interface MultichannelBlockProps {
	isActive?: boolean;
	onSubmit?: (withCertificate?: boolean, safeCredentialType?: SafeCredentialTypeEnum) => void;
}

export const MultichannelBlock = ({ isActive, onSubmit }: MultichannelBlockProps) => {
	const { getSpacing } = useCFPValues();
	const { t } = useTranslation();
	const { blocks, goto } = useBlockStepManager();
	const accountLogin = useServicesStore((state) => state.accountLogin) || {};
	const { alias, multichannel, showCaptcha, captchaValue, invalidDataAlert, onContinueClick, setState } =
		useLoginForm();
	const clientId = useSearchParamsStore((store) => store.clientId);
	const language = useSearchParamsStore((store) => store.language);

	const corporateClientsList = t('configs.corporateClientsList');
	const isCorporateClient = AppHelper.isCorporateClient(corporateClientsList, clientId);

	const handleSegmentedInputOnChange = (value?: string) => {
		/* only allow numeric characters to be inserted in the input */
		const numericRegex = /^[0-9]+$/;
		if (!value || numericRegex.test(value)) {
			setState({
				form: { multichannel: value || '' },
			});
		}
	};

	const handleCaptchaChange = (value: string) => {
		setState({ form: { captchaValue: value } });
	};

	const handleClosedContentLinkClick = () => {
		goto(LoginBlock.Multichannel);
	};

	const handleOpenContentButtonClick = async () => {
		if (accountLogin.needsSafe) {
			goto(LoginBlock.Safe);
		} else {
			onContinueClick(onSubmit);
		}
	};

	const openContentLinks: CollapsibleBlockLink[] = useMemo(() => {
		const links: CollapsibleBlockLink[] = [];

		if (isCorporateClient) {
			const extractPathSegment = (url: string): string => {
				const match = url.match(/\/(a|b)\//);
				return match?.length && match.length > 1 ? `${match[1]}/` : '';
			};

			const geo = extractPathSegment(window.location.pathname);

			const params = [
				{ key: 'alias', value: alias },
				{ key: 'cred', value: 'mtc' },
				{ key: 'language', value: language?.toLowerCase() },
			];

			const redirectUrl = t(resources.links.corporate.url, {
				geo,
				searchParams: UrlHelper.createQueryParams(params),
			});

			links.push({
				label: t(resources.links.corporate.text),
				href: redirectUrl,
			});
		} else {
			for (let i = 1; i <= 5; i++) {
				const label = t(`${resources.links.retail}.${i}.text`, undefined, undefined, true);
				const href = t(`${resources.links.retail}.${i}.url`, undefined, undefined, true);

				if (label && href) {
					links.push({ label, href });
				}
			}
		}

		return links;
	}, [alias, isCorporateClient, language, t]);

	const closedContentValue =
		multichannel &&
		Array.from(multichannel)
			.map(() => '*')
			.join('');

	const openContentButtonDisabled =
		!multichannel ||
		multichannel.length < (accountLogin.positionsToAsk?.length || 0) ||
		(showCaptcha && !captchaValue);

	const isLastBlock = blocks && blocks.length > 0 && blocks[blocks.length - 1].name === LoginBlock.Multichannel;

	return (
		<CollapsibleBlock
			isOpen={isActive}
			openContent={{
				label: {
					value: t(resources.title),
					tooltip: {
						title: t(resources.tooltip.title),
						description: t(resources.tooltip.description),
					},
				},
				links: openContentLinks,
				button: isLastBlock
					? {
							label: t(resources.button.continue),
							disabled: openContentButtonDisabled,
							onClick: handleOpenContentButtonClick,
					  }
					: undefined,
				children: (
					<Box styles={{ flexDirection: 'column', gap: UIHelper.rem(32) }}>
						<AppSegmentedInput
							length={accountLogin.positionsToAsk?.length}
							mask="numeric"
							secureTextEntry
							renderLabel={(index) => `${accountLogin.positionsToAsk?.[index]}ª`}
							value={multichannel || ''}
							onChange={handleSegmentedInputOnChange}
							autoFocus
						/>

						{!invalidDataAlert.isVisible && (
							<Alert type="info">
								<Box
									styles={{
										marginTop: getSpacing({
											default: {
												default: 2,
												mobile: 2,
												tablet: 7,
												laptop: 5,
											},
										}),
									}}
								>
									<Alert.Description text={t(resources.info.text)} />
								</Box>
							</Alert>
						)}

						{isLastBlock && <InvalidDataAlert />}

						{isLastBlock && showCaptcha && (
							<Box styles={{ marginTop: UIHelper.rem(32) }}>
								<Captcha value={captchaValue} onChange={handleCaptchaChange} />
							</Box>
						)}
					</Box>
				),
			}}
			closedContent={{
				label: t(resources.closed.title),
				value: closedContentValue,
				link: {
					label: t(resources.closed.link.text),
					onClick: handleClosedContentLinkClick,
				},
			}}
		/>
	);
};
